import { setFilters } from "../slice/HomeSlice";

// Common filter handlers
export const createHandleSelect = (dispatch, activeTab, setIsLoading, handleAPICalls, handleError) => {
  return async ({ label, value }) => {
    setIsLoading(true);

    dispatch(setFilters({
      contextKey: activeTab,
      filters: { [label]: value, page: 1 }
    }));
    try {
      await handleAPICalls(activeTab);
    } catch (error) {
      console.error(`Error in createHandleSelect for tab ${activeTab}:`, error);
      if (handleError) {
        handleError(error.message || "Failed to update filters");
      }
    }
  };
};

export const createHandleFilterChange = (dispatch, activeTab, setIsLoading, handleAPICalls, handleError) => {
  return async ({ label, value }) => {
    setIsLoading(true);
    let filterData = { [label]: value, page: 1 }
    if(label === 'page') {
      filterData.page = value;
    }
    dispatch(setFilters({
      contextKey: activeTab,
      filters: filterData
    }));
    try {
      await handleAPICalls(activeTab);
    } catch (error) {
      console.error(`Error in createHandleFilterChange for tab ${activeTab}:`, error);
      if (handleError) {
        handleError(error.message || "Failed to update filters");
      }
    }
  };
};

export const createHandlePageSizeChange = (
  dispatch,
  activeTab,
  handleAPICalls,
  setPageSizeForAll
) => {
  return async (pageSize) => {
    try {
      dispatch(setPageSizeForAll(pageSize));

      // refresh only the active tab
      await handleAPICalls(activeTab);
    } catch (error) {
      console.error(`Error in handlePageSizeChange for tab ${activeTab}:`, error);
    }
  };
};

export const createHandleStateChange = (dispatch, activeTab) => {
  return ({ label, value }) => {
    let filterData = { [label]: value }
    dispatch(setFilters({
      contextKey: activeTab,
      filters: filterData
    }));
  };
};

export const createOnSort = (dispatch, filterState, activeTab, setIsLoading, handleAPICalls, handleError) => {
  return async (sortBy) => {
    setIsLoading(true);
    const current = filterState[activeTab] || {};
    const sameColumn = !sortBy || sortBy === current.sort_by;
    const sortOrder = sameColumn && current.sort_direction === "asc" ? "desc" : "asc";
    const filters = { sort_direction: sortOrder, page: 1 };
    if (sortBy) {
      filters.sort_by = sortBy;
    }
    dispatch(setFilters({
      contextKey: activeTab,
      filters
    }));
    try {
      await handleAPICalls(activeTab);
    } catch (error) {
      console.error(`Error in createOnSort for tab ${activeTab}:`, error);
      if (handleError) {
        handleError(error.message || "Failed to sort data");
      }
    }
  };
};

export const createHandleBellClickGeneric = (options) => {
  const {
    dispatch,
    remindField,
    refreshAction,
    setIsLoading,
    setError,
    setShowNotification,
    handleError,
    errorContext = "Error sending reminders",
    remindAction, // pass remindReview or remindSignature
    perReviewer = true, // true: loop, false: once per record
    getSignatureId // function to get signature_request_id if needed
  } = options;

  return async (record) => {
    const remindReviewers = record[remindField].filter((r) => r.can_remind);

    try {
      setIsLoading(true);
      if (perReviewer) {
        const reminderPromises = remindReviewers.map((reviewer) =>
          dispatch(remindAction(reviewer.document_share_id)).unwrap()
        );
        await Promise.all(reminderPromises);
      } else if (remindReviewers.length > 0) {
        await dispatch(remindAction(getSignatureId ? getSignatureId(record) : record.signature_request_id)).unwrap();
      }

      await new Promise((resolve) => setTimeout(resolve, 3000));
      await dispatch(refreshAction());
      const res = {
        id: Date.now().toString(),
        newMsg: `Reminders sent successfully`,
        success: true,
      };
      setError([res]);
      setShowNotification(true);
    } catch (error) {
      console.error(`${errorContext}:`, error);
      handleError(error?.response?.data?.message || "Failed to send reminders");
    } finally {
      setIsLoading(false);
    }
  };
};

export const animateProgress = (from, to, duration, callback) => {
  const start = Date.now();
  const step = () => {
    const now = Date.now();
    const progress = Math.min(1, (now - start) / duration);
    const value = from + (to - from) * progress;
    callback(Math.floor(value));

    if (progress < 1) {
      requestAnimationFrame(step);
    }
  };
  requestAnimationFrame(step);
};

export const validateRequired = (value, fieldName) => {
    if (!value?.trim()) {
      return `${fieldName} is required.`;
    }
    return null;
  };

export const validateSSN = (ssn) => {
    if (!ssn?.trim()) {
      return 'Veteran File Number/SSN is required.';
    }
    if (!/^[0-9-]+$/.test(ssn)) {
      return 'Only numbers and dashes allowed.';
    }
    if (!/^[\d-]{8,12}$/.test(ssn)) {
      return 'Format must be XXX-XX-XXXX.';
    }
    return null;
  };

export const validateZipCode = (zipCode) => {
    if (!zipCode?.trim()) {
      return 'Zip code is required.';
    }
    if (!/^\d{5}(-\d{4})?$/.test(zipCode)) {
      return 'ZIP code must be in format 12345 or 12345-6789';
    }
    return null;
  };
